import * as React from 'react';
import { css, StyleSheet } from 'aphrodite/no-important';
import { HeaderLinksDataProps } from '../../components/navigation/header/Header';
import { LogoProps } from '../../components/navigation/header/Logo';
import { PrimaryHeaderThemeProps } from '../../components/navigation/header/primaryHeader/PrimaryHeader';
import { CtaButton, CtaDataProps } from '../../components/navigation/header/primaryHeader/CtaButton';
import { useTheme } from '../../../theming/useTheme';
import { ErrorBoundary } from '../../errorHandling/ErrorBoundary';
import { primaryHeaderHeight } from '../../components/navigation/header/header.utils';
import { MEDIA_BREAKPOINTS } from '../../../mediaQueries.const';
import { AMPLogo } from './AMPLogo';
import { AMPPrimaryLinks } from './AMPPrimaryLinks';
import { EditionDataProps } from '../../components/navigation/header/primaryHeader/Editions';
import { AMPEditions } from './AMPEditions';
import { AMPMenuIcon } from './AMPMenuIcon';
import { createStyleForBoxShadow } from '../../../theming/boxShadow';
import { BaseUnit } from '../../../theming/baseUnitDefinition';

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

interface AMPPrimaryHeaderDataProps {
  links: HeaderLinksDataProps[] | null
  logo: LogoProps
  editions: EditionDataProps[] | null
  cta: CtaDataProps | null
  shouldShowClosedMenuIcon: boolean
  pagePath: string
  editionEndpoint: string
}

type AMPPrimaryHeaderProps = AMPPrimaryHeaderDataProps & PrimaryHeaderThemeProps;

const componentConfigThemeKey = 'primaryHeader';

const getPrimaryHeaderStyle = (backgroundColor: string, baseUnit: BaseUnit) => StyleSheet.create({
  wrapper: {
    backgroundColor,
    position: 'relative',
    height: `${primaryHeaderHeight}px`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    zIndex: 100,
  },
  itemsBasicContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    height: '100%',
    width: 'fit-content',
    alignItems: 'center',
    gridColumnGap: `${baseUnit}px`,
  },
  itemsRightContainer: {
    alignItems: 'baseline',
    [MEDIA_BREAKPOINTS.LARGE]: {
      paddingRight: `${baseUnit}px`,
    },
    [MEDIA_BREAKPOINTS.MEDIUM]: {
      paddingRight: `${baseUnit / 2}px`,
    },
    [MEDIA_BREAKPOINTS.SMALL]: {
      paddingRight: `${baseUnit / 2}px`,
    },
  },
  itemsLeftContainer: {
    paddingLeft: `${baseUnit}px`,
  },
  ctaWrapper: {
    height: `${primaryHeaderHeight}px`,
    display: 'flex',
    alignItems: 'center',
  },
});

export const AMPPrimaryHeaderComponent: React.FunctionComponent<AMPPrimaryHeaderProps> = props => {
  const { links, logo, cta, boxShadowTypeNormal, backgroundColor, baseUnit, shouldShowClosedMenuIcon, editions, pagePath, editionEndpoint } = props;
  const style = getPrimaryHeaderStyle(backgroundColor, baseUnit);
  const boxShadowStyle = createStyleForBoxShadow({ boxShadowTypeNormal });
  return (
    <div className={css(style.wrapper, boxShadowStyle.style)}>
      <div className={css(style.itemsBasicContainer, style.itemsLeftContainer)}>
        {shouldShowClosedMenuIcon ? <AMPMenuIcon /> : null}
        <AMPLogo {...logo} />
        {links ? <AMPPrimaryLinks links={links} pagePath={pagePath} /> : null}
      </div>
      <div className={css(style.itemsBasicContainer, style.itemsRightContainer)}>
        {cta ? (
          <div className={css(style.ctaWrapper)}>
            <CtaButton {...cta} />
          </div>
        ) : null}
        {editions ? <AMPEditions editions={editions} editionEndpoint={editionEndpoint} /> : null}
      </div>
    </div>
  );
};

const mapThemeProps = (theme: PrimaryHeaderThemeProps) => {
  return {
    baseUnit: theme.baseUnit,
    backgroundColor: theme.backgroundColor,
    boxShadowTypeNormal: theme.boxShadowTypeNormal,
  };
};

export const AMPPrimaryHeader = (props: AMPPrimaryHeaderDataProps) => {
  const themeContext = useTheme(mapThemeProps, componentConfigThemeKey);
  return <ErrorBoundary><AMPPrimaryHeaderComponent {...themeContext} {...props} /></ErrorBoundary>;
};
